import { useState, useEffect, useRef, useCallback } from "react";
import { dateNumeric } from "../utils/formatDate";
import { UI } from "../constants/tokens";
import { TICKER_DATA } from "../constants/data";
import { useFxRates, fxAnswered } from "../utils/fxRates";
import { Amount } from "./Money";
import { fetchMarketData } from "../utils/marketdata";
import { useIsMobile } from "../utils/hooks";
import { useMarketStatus, sessionCloseMark, staleDate } from "../utils/marketStatus";

const fmt = n => n >= 1000
  ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : n.toFixed(2);

const TICKER_META = {
  "USD/ILS":   { desc: "שער חליפין דולר אמריקאי / שקל ישראלי" },
  "EUR/ILS":   { desc: "שער חליפין אירו / שקל ישראלי" },
  "S&P 500":   { desc: "מדד 500 החברות הגדולות בארצות הברית" },
  "NASDAQ":    { desc: "מדד NASDAQ Composite · כל המניות הנסחרות ב-NASDAQ" },
  "DOW":       { desc: "מדד דאו ג׳ונס התעשייתי · 30 חברות" },
  "ת״א 35":    { desc: "35 החברות הגדולות בבורסה בתל אביב · Yahoo Finance, בעיכוב של 20 דקות" },
  "Oil (WTI)": { desc: "מחיר נפט גולמי WTI לחבית (USD)" },
  "10Y UST":   { desc: "תשואת אגרת חוב אמריקאית ל-10 שנים" },
  "VIX":       { desc: "מדד הפחד · תנודתיות צפויה של S&P 500" },
};

export default function TickerTape() {
  const isMobile = useIsMobile();
  const market = useMarketStatus();
  const [live,    setLive]    = useState({});
  const fx = useFxRates();
  const [paused,  setPaused]  = useState(false);
  const [tooltip, setTooltip] = useState(null); // { item, x, y }
  // ─── Why the strip waits ─────────────────────────────────────────────────
  // The order of the rows is fixed — it is TICKER_DATA, an array. What was not
  // fixed was where the strip STARTED: it began scrolling immediately with
  // placeholder values ("…"), and the real numbers landed from two different
  // feeds a moment later. Each arrival changed the row widths under a running
  // animation whose keyframe is a percentage of that width, so the strip
  // jumped and a different row was under the eye on every load — VIX first
  // one time and last the next.
  //
  // Holding the animation until the first payload settles means the width is
  // final before the scroll begins, so every load starts from the same place.
  //
  // ─── Why it waits for three things, and moves in pixels (bug round, 17/10) ─
  // The strip moved by −50% of its own width, and began as soon as the market
  // data landed. Two more things changed the width after that: the FX rates
  // (USD/ILS and EUR/ILS, at the head of the row, so every item after them
  // moved) and the web fonts (font-display: swap, so Archivo replaced the
  // fallback mid-scroll). Every 5-minute refresh changed it again. On iOS
  // Safari a wide layer moving by transform is painted in tiles, and a layout
  // change under a running animation left old tiles beside new ones: text of
  // one item drawn over another ("OIL ,140.03", "15.6(WTI)"). Sprint 17's
  // flexShrink fix assumed items were squeezed, which a max-content row never
  // does, and could not explain text from two different items overlapping.
  //
  // Now: the scroll starts only after the market data, the FX rates AND the
  // fonts are in; one copy of the row is measured and the strip moves by
  // exactly that many pixels; and any later change waits for the loop end
  // and a new strip (sprint 21, below).
  const [mdDone, setMdDone] = useState(false);
  const [fontsDone, setFontsDone] = useState(false);
  // A cap, so a feed that never answers cannot freeze the strip: after it the
  // scroll starts anyway, and a later change of width rebuilds it.
  const [capped, setCapped] = useState(false);
  const fxDone = fxAnswered();
  const ready = (mdDone && fxDone && fontsDone) || capped;
  const tooltipRef = useRef(null);

  useEffect(() => {
    let alive = true;
    // `fonts.ready` alone resolves once no font is loading at that moment,
    // and the number font is requested only when the first digits render,
    // which is after the data: the strip could start, and then widen mid-loop
    // when Archivo arrived (22/10/2026). The faces the strip uses are asked
    // for by name first, then `ready` is awaited.
    const want = ["600 11px Archivo", "500 11px Archivo", "700 10px Assistant", "600 10px Assistant"];
    const fonts = typeof document !== "undefined" && document.fonts?.load
      ? Promise.all(want.map(f => document.fonts.load(f, "0123456789.,%$▲▼ S&P"))).then(() => document.fonts.ready)
      : Promise.resolve();
    fonts.then(() => { if (alive) setFontsDone(true); }, () => { if (alive) setFontsDone(true); });
    const t = setTimeout(() => { if (alive) setCapped(true); }, 10000);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  // ─── The moving row never changes while it moves (sprint 21, 4a) ────────
  // Sprint 19 kept the strip running and patched it in place: a change of
  // width under 2% updated the distance of the running animation
  // (setKeyframes), and each item grew to the widest it had been (minWidth).
  // Both are a change of layout under a moving layer, which is exactly what
  // iOS Safari paints in stale tiles · on the phone the tape still showed
  // "VIXL (WTI)" and "U· 14.10", letters of one item over another.
  //
  // Sprint 21 made the moving row a snapshot, and rebuilt it at the loop end
  // only. It held while the numbers stood still, and not while the market
  // was open: on the iPhone the tape still drew "OIL (WTIASDAQ" and
  // "S&P 5(10.31%" with the measured layout clean (no item overlapping
  // another · overlaps 0), that is, Safari's painting of a wide layer moved
  // by a transform animation (22/10/2026).
  //
  // So the strip does not move as a layer any more. The tape is a container
  // whose overflow is hidden, and each frame sets its scrollLeft: content that
  // scrolls is painted the way every scrolled page is, and a value that
  // changes is painted where it is. Still a snapshot (`shown`), and new
  // values still go in at the loop point only, where the first copy is back
  // at the start · so nothing jumps under the eye mid-loop. The tooltip reads
  // the live values.
  const SPEED = 28;                                 // px per second
  const scrollerRef = useRef(null);
  const copyRef = useRef(null);
  const [shown, setShown] = useState(null);        // the items the strip shows
  const latestRef = useRef(null);
  const pendingRef = useRef(false);                // new values wait for the loop point
  const pausedRef = useRef(false);
  const posRef = useRef(0);
  // The latest render's `freeze` (it reads the market status of that render).
  const freezeRef = useRef(list => list);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    if (!shown) return undefined;
    const el = scrollerRef.current;
    if (!el) return undefined;
    let raf = 0;
    let last = performance.now();
    const step = t => {
      const dt = Math.min(100, t - last);            // a tab in the background does not jump ahead
      last = t;
      const w = copyRef.current ? copyRef.current.getBoundingClientRect().width : 0;
      if (w > 0 && !pausedRef.current && !document.hidden) {
        let pos = posRef.current + (SPEED * dt) / 1000;
        if (pos >= w) {
          pos -= w;
          // The loop point: the first copy is at the start again.
          if (pendingRef.current) { pendingRef.current = false; setShown(freezeRef.current(latestRef.current)); }
        }
        posRef.current = pos;
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [!!shown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      // US indices + rates: end-of-day FRED via /.netlify/functions/marketdata.
      // Both value AND change come from the same source — no more 2024-vintage
      // value paired with a fresh percent.
      let md = {};
      try { md = (await fetchMarketData()) || {}; } catch { /* the rows say "—" */ }
      const next = {};
      if (md.sp500)       next.sp500       = { val: fmt(md.sp500.value),       chg: md.sp500.change_pct,       date: md.sp500.date,  ts: md.sp500.quote_ts  };
      if (md.nasdaq)      next.nasdaq      = { val: fmt(md.nasdaq.value),      chg: md.nasdaq.change_pct,      date: md.nasdaq.date, ts: md.nasdaq.quote_ts };
      if (md.dow)         next.dow         = { val: fmt(md.dow.value),         chg: md.dow.change_pct,         date: md.dow.date,    ts: md.dow.quote_ts    };
      if (md.ta35)        next.ta35        = { val: fmt(md.ta35.value),        chg: md.ta35.change_pct,        date: md.ta35.date,   ts: md.ta35.quote_ts   };
      // A yield moves in BASIS POINTS, not in percent of itself. 5.01 → 4.96
      // is five basis points; printing "−1.00%" beside "4.96%" reads as a full
      // percentage point off the yield, which is twenty times the truth.
      if (md.treasury10y) next.treasury10y = {
        val: `${md.treasury10y.value.toFixed(2)}%`,
        chg: md.treasury10y.change_pct,
        bp:  Number.isFinite(md.treasury10y.value_prev)
          ? Math.round((md.treasury10y.value - md.treasury10y.value_prev) * 100)
          : null,
        date: md.treasury10y.date, ts: md.treasury10y.quote_ts,
      };
      if (md.vix)         next.vix         = { val: md.vix.value.toFixed(2),                chg: md.vix.change_pct,         date: md.vix.date, ts: md.vix.quote_ts };
      if (md.oilWti)      next.oilWti      = { val: `$${md.oilWti.value.toFixed(2)}`, amount: { value: md.oilWti.value, currency: "USD", decimals: 2 }, chg: md.oilWti.change_pct, date: md.oilWti.date };
      setLive(prev => ({ ...prev, ...next }));

      setMdDone(true);

    };

    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Close tooltip on outside click
  useEffect(() => {
    if (!tooltip) return;
    const handler = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltip(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tooltip]);

  // ─── How a change is stated ────────────────────────────────────────────────
  // Three rules, all of them about not claiming more than the data says:
  //   · a yield is quoted in basis points, not in percent of itself
  //   · a change that rounds to 0.00% is NO CHANGE — no arrow, no colour.
  //     The S&P moved −0.0008% one day and the tape showed a red ▼ 0.00%:
  //     a direction with nothing behind it.
  //   · green and red are price movement only, so they appear only when the
  //     move is real (VISION.md).
  const changeCell = (item, big = false) => {
    if (item.change == null) return null;
    const bp   = Number.isFinite(item.bp) ? item.bp : null;
    const flat = bp != null ? bp === 0 : Math.abs(item.change) < 0.005;
    const up   = (bp != null ? bp : item.change) > 0;
    // No change is stated in words, in the neutral ink, with no plate behind
    // it · "0 נ״ב" on a tinted chip still read as a movement (ROADMAP,
    // "לתיקון", 2026-09-24).
    if (flat) {
      return (
        <span style={{ color: UI.text.muted, fontSize: big ? 13 : 10, fontWeight: 600 }}>ללא שינוי</span>
      );
    }
    const text = bp != null
      ? `${Math.abs(bp)} נ״ב`
      : `${Math.abs(item.change).toFixed(2)}%`;
    return (
      <span className="si-num" style={{
        color: up ? UI.gain : UI.loss,
        fontSize: big ? 13 : 10, fontWeight: 700,
        ...(big ? {
          background: up ? UI.gainSoft : UI.lossSoft,
          borderRadius: 6, padding: "2px 8px",
        } : null),
      }}>
        {up ? "▲ " : "▼ "}{text}
      </span>
    );
  };

  // ─── How old is this number ────────────────────────────────────────────────
  // Two kinds of answer, because there are two kinds of series.
  //
  // An INTRADAY quote carries the moment the exchange stamped it. More than
  // twenty minutes old means it is not "now" — the market is closed, or the
  // feed stopped — and the tape prints the time instead of letting the number
  // pass as current. This is the case that was invisible: for a whole session
  // the indices showed yesterday's close with nothing saying so.
  //
  // An END-OF-DAY observation has only a date, and yesterday's is normal
  // (Friday's shows until Monday). Older than three days is a source that
  // stopped publishing, and the tape prints the date.
  const WEEKDAY_HE = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
  const STALE_MIN  = 20;
  const pad2 = n => String(n).padStart(2, "0");
  // The quote's own clock · "23:42", or "שישי 23:42" when it is not from
  // today, so a Saturday screen does not read Friday night's quote as this
  // morning's.
  const quoteClock = item => {
    const t = new Date(item.quoteTs);
    if (!Number.isFinite(t.getTime())) return null;
    const hhmm = `${pad2(t.getHours())}:${pad2(t.getMinutes())}`;
    const sameDay = t.toDateString() === new Date().toDateString();
    return sameDay ? hhmm : `${WEEKDAY_HE[t.getDay()]} ${hhmm}`;
  };
  // While the market is not open, every index from the last session wears
  // the same label · "סגירה · שישי 16/10" · instead of the minute its
  // provider stamped the final value (utils/marketStatus.js, closeLabel).
  // The exact time is in the tooltip.
  // The US session's label is not Tel Aviv's (timeAlways · ת״א 35).
  const closeMark = item => (item.quoteTs && !item.timeAlways ? sessionCloseMark(item.quoteTs, market) : null);
  const staleMark = item => {
    if (item.timeAlways && item.quoteTs) return quoteClock(item);
    if (item.quoteTs) {
      const close = closeMark(item);
      if (close) return close;
      const t = new Date(item.quoteTs);
      if (!Number.isFinite(t.getTime())) return null;
      if (Date.now() - t.getTime() <= STALE_MIN * 60000) return null;
      return quoteClock(item);
    }
    // An end-of-day figure (FRED: oil, the 10-year yield) more than one
    // session behind says its date (20/10/2026 · the rule was three calendar
    // days, and a figure two sessions old passed as current).
    if (!item.asOf) return null;
    return staleDate(item.asOf, market) ? `נכון ל-${item.asOf.slice(8, 10)}/${item.asOf.slice(5, 7)}` : null;
  };

  // An item as the moving strip shows it, its time mark fixed.
  const freeze = list => (list || []).map(item => ({ ...item, mark: staleMark(item) || null }));
  freezeRef.current = freeze;

  const items = TICKER_DATA.map(item => {
    if (item.label === "S&P 500" && live.sp500)
      return { ...item, value: live.sp500.val,       change: live.sp500.chg,       asOf: live.sp500.date,  quoteTs: live.sp500.ts  };
    if (item.label === "NASDAQ"  && live.nasdaq)
      return { ...item, value: live.nasdaq.val,      change: live.nasdaq.chg,      asOf: live.nasdaq.date, quoteTs: live.nasdaq.ts };
    if (item.label === "DOW"     && live.dow)
      return { ...item, value: live.dow.val,         change: live.dow.chg,         asOf: live.dow.date,    quoteTs: live.dow.ts    };
    // Tel Aviv: its own clock, not the US session's · the time of the last
    // quote always (timeAlways), since there is no live source for whether
    // the Tel Aviv market is trading (DECISIONS.md, sprint 19).
    if (item.label === "ת״א 35" && live.ta35)
      return { ...item, value: live.ta35.val, change: live.ta35.chg, asOf: live.ta35.date, quoteTs: live.ta35.ts, timeAlways: true };
    if (item.label === "10Y UST" && live.treasury10y)
      return { ...item, value: live.treasury10y.val, change: live.treasury10y.chg,
               bp: live.treasury10y.bp, asOf: live.treasury10y.date, quoteTs: live.treasury10y.ts };
    // FX from the app's one rate store (utils/fxRates.js): the same rates,
    // from the same source, as every converted figure on screen.
    if (item.label === "USD/ILS" && fx.ILS)
      return { ...item, value: `₪${fx.ILS.rate.toFixed(3)}`, amount: { value: fx.ILS.rate, currency: "ILS", decimals: 3 }, change: fx.ILS.change_pct ?? undefined, liveDesc: fx.ILS.source };
    if (item.label === "EUR/ILS" && fx.ILS && fx.EUR)
      return { ...item, value: `₪${(fx.ILS.rate / fx.EUR.rate).toFixed(3)}`, amount: { value: fx.ILS.rate / fx.EUR.rate, currency: "ILS", decimals: 3 }, liveDesc: fx.ILS.source };
    if (item.label === "VIX" && live.vix)
      return { ...item, value: live.vix.val, change: live.vix.chg, asOf: live.vix.date, quoteTs: live.vix.ts };
    if (item.label === "Oil (WTI)" && live.oilWti)
      return { ...item, value: live.oilWti.val, amount: live.oilWti.amount, change: live.oilWti.chg, asOf: live.oilWti.date };
    // A live row the feeds did not fill. "…" only while the first payload is
    // still in flight; after that it is "—", and the tooltip says why.
    if (item.value === "…" && mdDone && fxDone) return { ...item, value: "—", noData: true };
    return item;
  });

  // What the strip shows of each item · a change of any of it is a new strip
  // at the next loop end.
  // The time mark is part of the snapshot (review): worked out live at each
  // render, "· 15:42" or "סגירה" would appear inside the moving layer.
  const contentOf = list => list.map(item => [item.label, item.value, item.change, item.bp, item.mark || "", item.noData ? "x" : ""].join("|")).join(";");
  latestRef.current = items;
  const liveKey = contentOf(freeze(items));
  const shownKey = shown ? contentOf(shown) : null;
  // The first strip once everything it waits for is in; after that, the new
  // values wait for the loop point (above).
  useEffect(() => {
    if (!ready) return;
    if (!shown) { setShown(freeze(items)); return; }
    pendingRef.current = liveKey !== shownKey;
  }, [ready, liveKey, shownKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemClick = useCallback((e, item) => {
    e.stopPropagation();
    if (tooltip?.item?.label === item.label) {
      setTooltip(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(rect.left, 8), window.innerWidth - 210);
    // The live values, not the strip's snapshot.
    setTooltip({ item: latestRef.current?.find(i => i.label === item.label) || item, x });
  }, [tooltip]);

  return (
    <div
      ref={scrollerRef}
      dir="ltr"
      // contain: paint · the moving layer is clipped to the strip and painted
      // apart from the page (sprint 21, 22/10).
      style={{ background: UI.surface.page, height: isMobile ? 36 : 30, overflow: "hidden", contain: "paint", position: "sticky", top: 0, zIndex: 101, borderBottom: `1px solid ${UI.line.hairline}`, cursor: "default" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Two copies side by side; the container scrolls by exactly the width
          of the first, then starts over. No transform and no layer of its
          own. Until the first strip is ready the live items stand still. */}
      <div
        style={{ display: "flex", alignItems: "center", height: "100%", whiteSpace: "nowrap", width: "max-content" }}>
        {[0, 1].map(copy => (
          <div key={copy} ref={copy === 0 ? copyRef : undefined} aria-hidden={copy === 1 ? "true" : undefined}
            style={{ display: "flex", alignItems: "center", height: "100%", flexShrink: 0 }}>
            {(shown || items).map(item => (
              <div
                key={item.label}
                onClick={(e) => handleItemClick(e, item)}
                // flexShrink 0 (sprint 17) stays: an item keeps its full width.
                // It was not what overlapped the text on the phone · that was
                // the width changing under a running animation ("Why it
                // waits", above).
                style={{ display: "inline-flex", flexShrink: 0, alignItems: "center", gap: 5, padding: "0 18px", borderRight: `1px solid ${UI.line.hairline}`, height: "100%", background: item.flag === "🇮🇱" ? UI.fill.subtle : "transparent", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                  boxSizing: "border-box", fontVariantNumeric: "tabular-nums" }}
              >
                {item.flag === "🇮🇱" && <span style={{ fontSize: 9 }}>🇮🇱</span>}
                {/* "This index is Israeli" is a category, not an insight — it
                    wears a categorical hue, never the reserved gold. */}
                {/* A Hebrew label is its own right-to-left run: in this
                    left-to-right strip "ת״א 35" came out "35 ת״א". */}
                <span dir={/[\u0590-\u05FF]/.test(item.label) ? "rtl" : undefined} style={{ color: item.flag === "🇮🇱" ? UI.categorical[0] : UI.text.muted, fontSize: 10, fontWeight: 700, letterSpacing: /[\u0590-\u05FF]/.test(item.label) ? 0 : ".5px", textTransform: "uppercase", unicodeBidi: "isolate" }}>{item.label}</span>
                {/* Tabular figures so a value that changes on refresh occupies exactly
                    the same width and never nudges the row it sits in. */}
                {item.amount
                  ? <Amount value={item.amount.value} currency={item.amount.currency} decimals={item.amount.decimals} size={11} weight={700} style={{ flexShrink: 0 }}/>
                  : <span className="si-num" style={{ color: UI.text.primary, fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{item.value}</span>}
                {changeCell(item)}
                {/* A number that is not from now carries its own time (or date)
                    in the row, so nobody has to tap to find that out. */}
                {(shown ? item.mark : staleMark(item)) && (
                  <span className="si-num" style={{ color: UI.text.muted, fontSize: 9.5, fontWeight: 700 }}>
                    · {shown ? item.mark : staleMark(item)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ── Ticker Tooltip ── */}
      {tooltip && (
        <div
          ref={tooltipRef}
          dir="rtl"
          style={{
            position: "fixed",
            top: isMobile ? 42 : 36,
            left: tooltip.x,
            zIndex: 200,
            background: UI.surface.raised,
            border: `1px solid ${UI.line.hairline}`,
            borderRadius: 6,
            padding: "10px 14px",
            minWidth: 200,
            maxWidth: 240,
            boxShadow: UI.shadow.pop,
            pointerEvents: "auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: UI.text.primary }}>{tooltip.item.label}</span>
            {tooltip.item.amount
              ? <Amount value={tooltip.item.amount.value} currency={tooltip.item.amount.currency} decimals={tooltip.item.amount.decimals} size={15} weight={800}/>
              : <span style={{ fontWeight: 800, fontSize: 15, color: UI.text.primary }}>{tooltip.item.value}</span>}
          </div>
          {tooltip.item.change != null && (
            <div style={{ marginBottom: 6 }}>{changeCell(tooltip.item, true)}</div>
          )}
          {TICKER_META[tooltip.item.label] && (
            <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.5 }}>
              {TICKER_META[tooltip.item.label].desc}
              {tooltip.item.liveDesc && <> · {tooltip.item.liveDesc}</>}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: UI.text.secondary }}>
            {tooltip.item.quoteTs
              ? closeMark(tooltip.item)
                ? `${closeMark(tooltip.item)} · הציטוט האחרון בשעה ${quoteClock(tooltip.item)} · לחץ שוב כדי לסגור`
                : (staleMark(tooltip.item)
                  ? `הציטוט האחרון בשעה ${staleMark(tooltip.item)} · לחץ שוב כדי לסגור`
                  : "ציטוט מהדקות האחרונות · לחץ שוב כדי לסגור")
              : tooltip.item.asOf
                ? (staleMark(tooltip.item)
                    ? `הנתון האחרון מ-${dateNumeric(tooltip.item.asOf)} · המקור לא עדכן מאז · לחץ שוב כדי לסגור`
                    : `נכון לסגירת המסחר ${dateNumeric(tooltip.item.asOf)} · לחץ שוב כדי לסגור`)
                : tooltip.item.noData
                  ? "אין נתון זמין כרגע · לחץ שוב כדי לסגור"
                  : "עדכון כל 5 דקות · לחץ שוב כדי לסגור"}
          </div>
        </div>
      )}
    </div>
  );
}
